type NumFormatOptions = {
	keepFractions?: boolean;
	fractionDigits?: number;
	hideFractIfInteger?: boolean;
};

function FormatWithThousandSeparator(value?: number | string | null, options?: NumFormatOptions) {
	const num = Number(value);

	if (!Number.isFinite(num)) {
		return 'N/A';
	}

	let stringifiedValue: string;

	if (options?.keepFractions) {
		stringifiedValue = num.toString();
	} else if (options?.hideFractIfInteger) {
		const [int, fract] = num.toString().split('.');
		stringifiedValue = fract ? num.toString() : int;
	} else if (typeof options?.fractionDigits === 'number') {
		stringifiedValue = num.toFixed(options.fractionDigits);
	} else {
		stringifiedValue = num.toFixed(2);
	}

	const decimalPointIndex = stringifiedValue.indexOf('.');

	return stringifiedValue.replace(/\d(?=(?:\d{3})+(?:\.|$))/g, (m, i) =>
		decimalPointIndex < 0 || i < decimalPointIndex ? `${m},` : m,
	);
}

type BigNumberFormatOptions = {
	fractionDigits?: number;
	trimTrailingZeroes?: boolean;
};

const formatCompactValue = (
	value: number,
	divisor: number,
	suffix: string,
	options?: BigNumberFormatOptions,
) => {
	const fractionDigits = options?.fractionDigits ?? 2;
	const compactValue = value / divisor;
	const formatted = compactValue.toFixed(fractionDigits);
	const normalized =
		options?.trimTrailingZeroes === false
			? formatted
			: formatted.replace(/\.0+$|(?<=\.[0-9]*[1-9])0+$/g, '');

	return `${normalized}${suffix}`;
};

// shorten big numbers like 1000 to 1K, 1000000 to 1M, 1000000000 to 1B, 1000000000000 to 1T
function formatBigNumbers(value?: number, options?: BigNumberFormatOptions) {
	if (typeof value !== 'number' || !Number.isFinite(value)) {
		return 'N/A';
	}
	if (value === 0) return '0';
	if (value >= 1e12) {
		return formatCompactValue(value, 1e12, 'T', options);
	} else if (value >= 1e9) {
		return formatCompactValue(value, 1e9, 'B', options);
	} else if (value >= 1e6) {
		return formatCompactValue(value, 1e6, 'M', options);
	} else if (value >= 1e3) {
		return formatCompactValue(value, 1e3, 'K', options);
	} else {
		return value.toString();
	}
}

export { FormatWithThousandSeparator, formatBigNumbers };
